/**
 * Thin fetch wrapper for the admin panel.
 *
 * - Injects the admin JWT from localStorage on every request.
 * - On 401, clears auth + redirects to /login (best-effort; final redirect
 *   is handled by the route guard once React re-renders).
 * - Throws an `ApiError` with `status` + parsed body for non-2xx responses
 *   so callers can branch without re-parsing.
 *
 * Wave 2 workers should layer TanStack Query on top of this — this module
 * does no caching and no retries. It is the lowest-level transport.
 */

import { getToken, logout } from '@/auth/admin-auth';
import { isReadOnlyAdminMode } from '@/lib/admin-mode';

const BASE_URL = import.meta.env.VITE_API_URL ?? '';

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Skip the auth header (used for /admin/login itself). */
  skipAuth?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, skipAuth, headers, ...rest } = options;
  const method = (rest.method ?? 'GET').toUpperCase();

  if (isReadOnlyAdminMode() && !['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    throw new ApiError(405, 'Production read-only mode blocked this admin write.', {
      code: 'ADMIN_READ_ONLY',
      path,
      method,
    });
  }

  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...((headers as Record<string, string>) ?? {}),
  };

  if (body !== undefined) {
    finalHeaders['Content-Type'] = 'application/json';
  }

  if (!skipAuth) {
    const token = getToken();
    if (token) finalHeaders.Authorization = `Bearer ${token}`;
  }

  const url = path.startsWith('http') ? path : `${BASE_URL}${path}`;

  const res = await fetch(url, {
    ...rest,
    headers: finalHeaders,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 401 && !skipAuth) {
    logout();
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
    throw new ApiError(401, 'Unauthorized', null);
  }

  const contentType = res.headers.get('content-type') ?? '';
  const parsed: unknown = contentType.includes('application/json')
    ? await res.json().catch(() => null)
    : await res.text().catch(() => null);

  if (!res.ok) {
    const message =
      (parsed as { error?: string } | null)?.error ?? `Request failed (${res.status})`;
    throw new ApiError(res.status, message, parsed);
  }

  return parsed as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};
